import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import API from '../utils/api';
import HeroSlider from '../components/HeroSlider';
import StoryShowcase from '../components/StoryShowcase';
import BackgroundAudio from '../components/BackgroundAudio';
import generateTxnRef from '../utils/generateTxnRef';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function Home() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({});
  const [recentDonations, setRecentDonations] = useState([]);
  const [cows, setCows] = useState([]);
  const [ways, setWays] = useState([]);
  const [whyCards, setWhyCards] = useState([]);
  const [txnRef] = useState(generateTxnRef);

  useEffect(() => {
    API.get('/settings').then(r => setSettings(r.data)).catch(() => {});
    API.get('/donations/recent').then(r => setRecentDonations(r.data)).catch(() => {});
    API.get('/cows?adoptable=true').then(r => setCows(r.data.slice(0, 3))).catch(() => {});
    API.get('/donation-ways').then(r => setWays(r.data)).catch(() => {});
    API.get('/why-cards').then(r => setWhyCards(r.data)).catch(() => {});
  }, []);

  // Shared by both the "Why Goshala" cards and the "Ways to Donate" cards —
  // their icon field holds either an uploaded image path or a plain emoji.
  const iconImageUrl = icon => (icon && (icon.startsWith('/uploads/') || icon.startsWith('http')))
    ? (icon.startsWith('http') ? icon : `${API_BASE}${icon}`)
    : null;
  const wayAmountText = w => w.amountLabel || (w.amount ? `₹${Number(w.amount).toLocaleString('en-IN')}` : '');
  const defaultCowImageUrl = settings.defaultCowImage
    ? (settings.defaultCowImage.startsWith('http') ? settings.defaultCowImage : `${API_BASE}${settings.defaultCowImage}`)
    : null;

  const purposeLabel = p => ({
    general: t('home.purposeGeneral'),
    cow_adoption: t('home.purposeCowAdoption'),
    fodder: t('home.purposeFodder'),
    medical: t('home.purposeMedical'),
  }[p] || p);
  const upiPaymentLink = `upi://pay?pa=${settings.upiId || '73966431056m@pnb'}&pn=Goshala&cu=INR&tr=${txnRef}`;
  const qrCodeImageUrl = settings.qrCodeImage ? (settings.qrCodeImage.startsWith('http') ? settings.qrCodeImage : `${API_BASE}${settings.qrCodeImage}`) : null;
  const backgroundAudioUrl = settings.backgroundAudio ? (settings.backgroundAudio.startsWith('http') ? settings.backgroundAudio : `${API_BASE}${settings.backgroundAudio}`) : null;

  return (
    <div>
      <BackgroundAudio src={backgroundAudioUrl} />

      {/* HERO */}
      <HeroSlider>
        <div className="hero-content">
          <div className="hero-badge">{t('home.badge')}</div>
          <h1 className="hero-title">{settings.heroTitle || t('home.welcomeTitle')}</h1>
          <div className="hero-title-hindi">गोसेवा • गोदान • गोरक्षा</div>
          <p className="hero-desc">
            {settings.heroSubtitle || t('home.heroDesc')}
          </p>
          <div className="hero-actions">
            <Link to="/donations" className="btn btn-primary btn-lg">{t('common.donateNow')}</Link>
            <Link to="/adopt-a-cow" className="btn btn-outline btn-lg">{t('common.adoptACow')}</Link>
          </div>
          <div className="hero-stats">
            <div className="hero-stat"><div className="hero-stat-number">1250+</div><div className="hero-stat-label">{t('home.statCowsSheltered')}</div></div>
            <div className="hero-stat"><div className="hero-stat-number">500+</div><div className="hero-stat-label">{t('home.statHappyDonors')}</div></div>
            <div className="hero-stat"><div className="hero-stat-number">11+</div><div className="hero-stat-label">{t('home.statYearsSeva')}</div></div>
          </div>
        </div>
        <div className="hero-qr-floating">
          <div className="hero-qr-floating-label">{t('home.scanDonate')}</div>
          {qrCodeImageUrl
            ? <img src={qrCodeImageUrl} alt="Donation QR code" style={{ width: 86, height: 86, objectFit: 'contain', margin: '6px 0' }} />
            : <QRCodeSVG value={upiPaymentLink} size={86} fgColor="#9B1B30" level="H" style={{ margin: '6px 0' }} />
          }
          <div className="hero-qr-floating-value">{settings.upiId || '73966431056m@pnb'}</div>
        </div>
      </HeroSlider>

      {/* STATS */}
      <section className="stats-banner">
        <div className="container">
          <div className="stats-grid">
            {[
              { num: '1250+', label: t('home.bannerCowsProtected') },
              { num: '15+', label: t('home.cowSheds') },
              { num: '₹20L+', label: t('home.bannerDonationsReceived') },
              { num: '365', label: t('home.bannerDaysOfSeva') },
              { num: '100%', label: t('home.bannerTransparent') },
            ].map((s, i) => (
              <div key={i} className="stat-item">
                <div className="stat-item-number">{s.num}</div>
                <div className="stat-item-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY GOSHALA */}
      {whyCards.length > 0 && (
        <section className="section" style={{ background: '#fff' }}>
          <div className="container">
            <h2 className="section-title"><Trans i18nKey="home.whyTitle" components={{ 1: <span /> }} /></h2>
            <p className="section-subtitle">{t('home.whySubtitle')}</p>
            <div className="grid-4" style={{ marginTop: 40 }}>
              {whyCards.map(item => (
                <div key={item._id} className="card" style={{ textAlign: 'center', padding: 24 }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>
                    {iconImageUrl(item.icon)
                      ? <img src={iconImageUrl(item.icon)} alt={item.title} style={{ width: 48, height: 48, objectFit: 'contain', margin: '0 auto' }} />
                      : (item.icon || '🙏')
                    }
                  </div>
                  <h3 style={{ fontWeight: 700, marginBottom: 8, color: '#1a1a1a' }}>{item.title}</h3>
                  <p style={{ color: '#777', fontSize: '0.9rem', lineHeight: 1.6 }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STORY SHOWCASE */}
      <StoryShowcase />

      {/* ADOPT A COW */}
      {cows.length > 0 && (
        <section className="section" style={{ background: '#FFF8F0' }}>
          <div className="container">
            <h2 className="section-title"><Trans i18nKey="home.adoptTitle" components={{ 1: <span /> }} /></h2>
            <p className="section-subtitle">{t('home.adoptSubtitle')}</p>
            <div className="grid-3">
              {cows.map(cow => (
                <div key={cow._id} className="cow-card">
                  {cow.image
                    ? <img src={`${API_BASE}${cow.image}`} alt={cow.name} className="cow-card-img" />
                    : defaultCowImageUrl
                      ? <img src={defaultCowImageUrl} alt={cow.name} className="cow-card-img" />
                      : <div className="cow-card-img-placeholder">🐂</div>
                  }
                  <div className="cow-card-body">
                    <div className="cow-card-name">{cow.name}</div>
                    <div className="cow-card-breed">{cow.breed} • {cow.age ? t('home.ageYears', { age: cow.age }) : t('home.ageUnknown')}</div>
                    <div className="cow-card-badges">
                      <span className={`badge badge-${cow.healthStatus === 'healthy' ? 'green' : 'orange'}`}>{cow.healthStatus}</span>
                      <span className="badge badge-brown">{cow.gender}</span>
                    </div>
                    <Link to={`/adopt-a-cow`} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
                      {t('home.adoptButton', { name: cow.name })}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Link to="/adopt-a-cow" className="btn btn-outline">{t('home.viewAllCows')}</Link>
            </div>
          </div>
        </section>
      )}

      {/* DONATION WAYS */}
      {ways.length > 0 && (
        <section className="section" style={{ background: '#fff' }}>
          <div className="container">
            <h2 className="section-title"><Trans i18nKey="home.waysTitle" components={{ 1: <span /> }} /></h2>
            <p className="section-subtitle">{t('home.waysSubtitle')}</p>
            <div className="grid-3" style={{ marginTop: 32 }}>
              {ways.map(d => (
                <div key={d._id} style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1.5px solid #e8e0d8', borderTopWidth: 4, borderTopColor: d.accentColor, textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>
                    {iconImageUrl(d.icon)
                      ? <img src={iconImageUrl(d.icon)} alt={d.title} style={{ width: 40, height: 40, objectFit: 'contain', margin: '0 auto' }} />
                      : (d.icon || '🎁')
                    }
                  </div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: d.accentColor, marginBottom: 4 }}>{wayAmountText(d)}</div>
                  <h4 style={{ fontWeight: 600, marginBottom: 8 }}>{d.title}</h4>
                  <p style={{ color: '#777', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: 16 }}>{d.description}</p>
                  <Link to="/donations" className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '8px 20px' }}>{t('home.donateButton')}</Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RECENT DONORS */}
      {recentDonations.length > 0 && (
        <section className="section" style={{ background: '#FFF8F0' }}>
          <div className="container">
            <h2 className="section-title"><Trans i18nKey="home.recentDonorsTitle" components={{ 1: <span /> }} /></h2>
            <p className="section-subtitle">{t('home.recentDonorsSubtitle')}</p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              {recentDonations.map((d, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '12px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: '1.5rem' }}>🙏</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{d.guestName}</div>
                    <div style={{ color: '#9B1B30', fontWeight: 700, fontSize: '1rem' }}>₹{d.amount?.toLocaleString('en-IN')}</div>
                    <div style={{ color: '#888', fontSize: '0.75rem' }}>{purposeLabel(d.purpose)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, #9B1B30, #D9720B)', padding: '60px 0', textAlign: 'center', color: '#fff' }}>
        <div className="container">
          <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 12 }}>{t('home.ctaTitle')}</h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9, marginBottom: 28, fontFamily: 'Noto Serif Devanagari, serif' }}>
            गावो विश्वस्य मातरः — Cows are the mothers of the universe
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/donations" className="btn" style={{ background: '#fff', color: '#9B1B30', fontWeight: 700, padding: '14px 36px' }}>{t('common.donateNow')}</Link>
            <Link to="/adopt-a-cow" className="btn btn-outline" style={{ color: '#fff', borderColor: '#fff', padding: '14px 36px' }}>{t('common.adoptACow')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
